// Worker 消息类型
interface DownloadProgressMessage {
    type: 'DOWNLOAD_PROGRESS';
    progress: number;
    loaded: number;
    total: number;
    speed: number;
}

interface ParseProgressMessage {
    type: 'PARSE_PROGRESS';
    progress: number;
    parsed: number;
    total: number;
    speed: number;
}

interface CompleteMessage {
    type: 'COMPLETE';
    totalItems: number;
    downloadTime: number;
    parseTime: number;
}

interface ErrorMessage {
    type: 'ERROR';
    error: string;
}

interface CrudResponseMessage {
    type: 'CRUD_RESPONSE';
    requestId: string;
    success: boolean;
    data?: any;
    error?: string;
}

interface CompleteCallback {
    type: 'COMPLETE';
    chunks: any[][];
}

type WorkerMessage = DownloadProgressMessage | ParseProgressMessage | CompleteMessage | ErrorMessage | CrudResponseMessage;

export interface BigJSONOptions {
    downloadOnly: boolean;
    chunkSize: number;
}

export class BigJSONUtils {
    // ---------- 私有状态 ----------
    private readonly options: BigJSONOptions = {
        downloadOnly: false,
        chunkSize: 100,
    };
    private worker: Worker | null = null;
    private readonly _pendingRequests = new Map<string, { resolve: (data: any) => void; reject: (err: Error) => void }>();
    private static readonly MAX_DISPLAY_CHUNKS = 20;

    // ---------- 公有状态（可读） ----------
    jsonUrl = 'https://res.gdpdf.com/pdf/view/19167.json';
    isProcessing = false;
    downloadProgress = 0;
    downloadLoaded = 0;
    downloadTotal = 0;
    downloadSpeed = 0;
    parseProgress = 0;
    parseParsed = 0;
    parseTotal = 0;
    parseSpeed = 0;
    totalProcessedItems = 0;
    processedChunks: any[][] = [];
    errorMessage = '';
    isComplete = false;

    // 这里需要回调方法
    onComplete: (data: CompleteCallback) => void = () => {};
    // 进度监听
    onProgress: (progress: number) => void = () => {};
    get previewCount(): number {
        return BigJSONUtils.MAX_DISPLAY_CHUNKS * this.options.chunkSize;
    }

    get displayChunks(): any[][] {
        return this.processedChunks.slice(0, BigJSONUtils.MAX_DISPLAY_CHUNKS);
    }

    get downloadProgressStatus(): string {
        if (this.errorMessage) return 'error';
        if (this.downloadProgress >= 100) return 'success';
        return 'default';
    }

    get parseProgressStatus(): string {
        if (this.errorMessage) return 'error';
        if (this.parseProgress >= 100) return 'success';
        return 'default';
    }

    // ============================================================
    // 私有：Worker CRUD 通信
    // ============================================================

    private callWorker(msg: Record<string, any>): Promise<any> {
        if (!this.worker) return Promise.reject(new Error('Worker 未初始化'));
        // 这里用时间戳做唯一id
        const requestId = Date.now().toString();
        return new Promise((resolve, reject) => {
            this._pendingRequests.set(requestId, { resolve, reject });
            this.worker!.postMessage({ ...msg, requestId });
        });
    }

    private handleCrudResponse(data: CrudResponseMessage) {
        const pending = this._pendingRequests.get(data.requestId);
        if (!pending) return;
        this._pendingRequests.delete(data.requestId);
        if (data.success) {
            pending.resolve(data.data);
        } else {
            pending.reject(new Error(data.error || '未知错误'));
        }
    }

    // ============================================================
    // 公有：数据操作 API（通过 Worker CRUD）
    // ============================================================

    async getTotal(): Promise<number> {
        const result = await this.callWorker({ type: 'GET_TOTAL' });
        return result.total;
    }

    async getRow(index: number): Promise<any> {
        const result = await this.callWorker({ type: 'GET_ROW', index });
        return result.row;
    }

    async getRows(start: number, count: number): Promise<{ rows: any[]; start: number; count: number; total: number }> {
        return await this.callWorker({ type: 'GET_ROWS', start, count });
    }

    async getAllItems(): Promise<{ rows: any[]; total: number }> {
        return await this.callWorker({ type: 'GET_ALL' });
    }

    async insertRow(index: number, row: any): Promise<{ index: number; total: number }> {
        return await this.callWorker({ type: 'INSERT_ROW', index, row });
    }

    async updateRow(index: number, row: any): Promise<{ index: number; total: number }> {
        return await this.callWorker({ type: 'UPDATE_ROW', index, row });
    }

    async updateCell(rowIndex: number, colIndex: number, value: any): Promise<{ rowIndex: number; colIndex: number; total: number }> {
        return await this.callWorker({ type: 'UPDATE_CELL', rowIndex, colIndex, value });
    }

    async deleteRow(index: number): Promise<{ deleted: any; index: number; total: number }> {
        return await this.callWorker({ type: 'DELETE_ROW', index });
    }

    async search(keyword: string, fields?: number[]): Promise<{ results: { index: number; row: any }[]; count: number }> {
        return await this.callWorker({ type: 'SEARCH', keyword, fields });
    }

    async handleClear() {
        try {
            await this.callWorker({ type: 'CLEAR' });
            this.processedChunks = [];
            this.totalProcessedItems = 0;
            this.isComplete = false;
        } catch (_err) {
            // 清除失败可忽略
        }
    }

    // ============================================================
    // 私有：从 Worker 加载预览数据
    // ============================================================

    private async loadPreviewFromWorker() {
        try {
            const previewRows = BigJSONUtils.MAX_DISPLAY_CHUNKS * this.options.chunkSize;
            const result = await this.callWorker({ type: 'GET_ALL' });
            const chunks: any[][] = [];
            const rows = result.rows as any[];
            for (let i = 0; i < rows.length; i += this.options.chunkSize) {
                chunks.push(rows.slice(i, i + this.options.chunkSize));
            }
            this.processedChunks = chunks;
            console.log("loadPreviewFromWorker", chunks);
            this.onComplete({
                type: 'COMPLETE',
                chunks: chunks,
            });
        } catch (err) {
            console.error('Failed to load preview from Worker:', err);
        }
    }

    // ============================================================
    // 公有：格式化工具
    // ============================================================

    formatBytes(bytes: number): string {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }

    formatSpeed(bytesPerSecond: number): string {
        return this.formatBytes(bytesPerSecond) + '/s';
    }

    formatParseSpeed(itemsPerSecond: number): string {
        if (itemsPerSecond >= 1000) {
            return (itemsPerSecond / 1000).toFixed(1) + 'K 项/s';
        }
        return Math.round(itemsPerSecond) + ' 项/s';
    }

    formatRemainingTime(seconds: number): string {
        if (seconds < 60) {
            return Math.ceil(seconds) + ' 秒';
        } else if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60);
            const secs = Math.ceil(seconds % 60);
            return minutes + ' 分 ' + secs + ' 秒';
        } else {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            return hours + ' 小时 ' + minutes + ' 分';
        }
    }

    formatJsonPreview(data: any[]): string {
        if (data.length === 0) return '[]';
        if (data.length === 1) {
            return JSON.stringify(data[0], null, 2);
        }
        const preview = data.slice(0, 3);
        const previewStr = JSON.stringify(preview, null, 2);
        if (data.length > 3) {
            return previewStr.replace(/]$/, `\n  // ... 还有 ${data.length - 3} 项\n]`);
        }
        return previewStr;
    }

    // ============================================================
    // Worker 交互
    // ============================================================

    async handleLoad(downloadOnly: boolean) {
        console.log(performance.now(), 'handleLoad');
        if (!this.jsonUrl.trim()) return;

        this.options.downloadOnly = downloadOnly;
        this.isProcessing = true;
        this.downloadProgress = 0;
        this.downloadLoaded = 0;
        this.downloadTotal = 0;
        this.downloadSpeed = 0;
        this.parseProgress = 0;
        this.parseParsed = 0;
        this.parseTotal = 0;
        this.parseSpeed = 0;
        this.totalProcessedItems = 0;
        this.processedChunks = [];
        this.errorMessage = '';
        this.isComplete = false;

        try {
            await this.processLargeJsonWithWorker(this.jsonUrl);
        } catch (error) {
            const errMsg = error instanceof Error ? error.message : String(error);
            this.errorMessage = `启动处理失败: ${errMsg}`;
            this.isProcessing = false;
            console.error('处理失败', errMsg);
        }
    }

    handleCancel() {
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
        }
        for (const [, pending] of this._pendingRequests) {
            pending.reject(new Error('Worker 已取消'));
        }
        this._pendingRequests.clear();
        this.isProcessing = false;
    }

    private async processLargeJsonWithWorker(url: string) {
        if (this.worker) {
            this.worker.terminate();
            this._pendingRequests.clear();
        }
        this.worker = new Worker('/JSONWorker.js');
        this.worker.postMessage({
            type: 'START_PROCESSING',
            url,
            options: {
                downloadOnly: this.options.downloadOnly,
                chunkSize: this.options.chunkSize,
                progressThrottle: 80,
            },
        });

        this.worker.onmessage = (e: MessageEvent<WorkerMessage>) => {
            switch (e.data.type) {
                case 'DOWNLOAD_PROGRESS':
                    this.updateDownloadProgress(e.data);
                    break;
                case 'PARSE_PROGRESS':
                    this.updateParseProgress(e.data);
                    break;
                case 'COMPLETE':
                    void this.handleComplete(e.data);
                    break;
                case 'ERROR':
                    this.handleError(e.data.error);
                    break;
                case 'CRUD_RESPONSE':
                    this.handleCrudResponse(e.data);
                    break;
            }
        };

        this.worker.onerror = (error) => {
            this.handleError(`Worker 错误: ${error.message || '未知错误'}`);
        };
    }

    private updateDownloadProgress(data: DownloadProgressMessage) {
        this.downloadProgress = Math.round(Math.min(data.progress, 100) * 100) / 100;
        this.downloadLoaded = data.loaded;
        this.downloadTotal = data.total;
        this.downloadSpeed = data.speed;
        this.onProgress(this.downloadProgress);
        console.log("updateDownloadProgress", data);
    }

    private updateParseProgress(data: ParseProgressMessage) {
        this.parseProgress = Math.min(data.progress, 100);
        this.parseParsed = data.parsed;
        this.parseTotal = data.total;
        this.parseSpeed = data.speed;
        console.log("updateParseProgress", data);
    }

    private async handleComplete(data: CompleteMessage) {
        console.log(performance.now(), 'handleComplete');
        this.isProcessing = false;
        this.isComplete = true;
        this.totalProcessedItems = data.totalItems;
        this.downloadProgress = 100;

        if (!this.options.downloadOnly) {
            this.parseProgress = 100;
            this.parseTotal = data.totalItems;
            this.parseParsed = data.totalItems;
            await this.loadPreviewFromWorker();
        } else {
            if (this.worker) {
                this.worker.terminate();
                this.worker = null;
            }
        }
    }

    private handleError(error: string) {
        this.isProcessing = false;
        this.errorMessage = error;
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
        }
        for (const [, pending] of this._pendingRequests) {
            pending.reject(new Error(error));
        }
        this._pendingRequests.clear();
    }

    // 设置url 并开始下载
    setJSONUrl(url: string) {
        this.jsonUrl = url;
        this.handleLoad(this.options.downloadOnly);
    }

    clearWorker() {
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
        }
        this._pendingRequests.clear();
    }

    testGetRow(index: number) {
        this.getRow(index - 1).then((data) => console.log('testGetRow', data));
    }

    testGetRows(start: number, count: number) {
        this.getRows(start - 1, count).then((data) => console.log('testGetRows', data));
    }
}
